import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Utility functions for managing tournament-specific admin permissions
 */

/**
 * Assign a user as admin to a specific tournament
 * @param {string} userId - The user ID to make tournament admin
 * @param {string} tournamentId - The tournament ID
 * @param {string} assignedBy - The user ID of the super admin assigning this
 * @returns {Promise<boolean>} - True if successful
 */
export const assignTournamentAdmin = async (userId, tournamentId, assignedBy) => {
  if (!userId || !tournamentId || !assignedBy) return false;

  try {
    const tournamentAdminRef = doc(db, 'tournamentAdmins', `${userId}_${tournamentId}`);
    await setDoc(tournamentAdminRef, {
      userId,
      tournamentId,
      assignedBy,
      assignedAt: new Date().toISOString(),
    });
    return true;
  } catch (error) {
    console.error('Error assigning tournament admin:', error);
    return false;
  }
};

/**
 * Revoke tournament admin privileges from a user
 * @param {string} userId - The user ID to remove tournament admin from
 * @param {string} tournamentId - The tournament ID
 * @returns {Promise<boolean>} - True if successful
 */
export const revokeTournamentAdmin = async (userId, tournamentId) => {
  if (!userId || !tournamentId) return false;

  try {
    const tournamentAdminRef = doc(db, 'tournamentAdmins', `${userId}_${tournamentId}`);
    await deleteDoc(tournamentAdminRef);
    return true;
  } catch (error) {
    console.error('Error revoking tournament admin:', error);
    return false;
  }
};

/**
 * Check if a user is admin for a specific tournament
 * @param {string} userId - The user ID to check
 * @param {string} tournamentId - The tournament ID
 * @returns {Promise<boolean>} - True if user is tournament admin
 */
export const isTournamentAdminForTournament = async (userId, tournamentId) => {
  if (!userId || !tournamentId) return false;

  try {
    const q = query(
      collection(db, 'tournamentAdmins'),
      where('userId', '==', userId),
      where('tournamentId', '==', tournamentId)
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking tournament admin status:', error);
    return false;
  }
};

/**
 * Get all tournaments a user administers
 * @param {string} userId - The user ID
 * @returns {Promise<string[]>} - Array of tournament IDs
 */
export const getUserTournamentAdminList = async (userId) => {
  if (!userId) return [];

  try {
    const q = query(collection(db, 'tournamentAdmins'), where('userId', '==', userId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => doc.data().tournamentId);
  } catch (error) {
    console.error('Error fetching user tournament admin list:', error);
    return [];
  }
};

/**
 * Get all admins for a specific tournament
 * @param {string} tournamentId - The tournament ID
 * @returns {Promise<Object[]>} - Array of tournament admin objects
 */
export const getTournamentAdmins = async (tournamentId) => {
  if (!tournamentId) return [];

  try {
    const q = query(collection(db, 'tournamentAdmins'), where('tournamentId', '==', tournamentId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching tournament admins:', error);
    return [];
  }
};

/**
 * Set up a real-time listener for tournament admin changes for a user
 * @param {string} userId - The user ID to listen for
 * @param {Function} callback - Callback function to handle changes
 * @returns {Function} - Unsubscribe function
 */
export const listenToUserTournamentAdmins = (userId, callback) => {
  if (!userId) return () => {};

  const q = query(collection(db, 'tournamentAdmins'), where('userId', '==', userId));

  return onSnapshot(
    q,
    (snapshot) => {
      const tournaments = snapshot.docs.map((doc) => doc.data().tournamentId);
      callback(tournaments);
    },
    (error) => {
      console.error('Error listening to tournament admin changes:', error);
      callback([]);
    }
  );
};

/**
 * Check if user has access to a tournament (either super admin or tournament admin)
 * @param {string} userId - The user ID
 * @param {string} tournamentId - The tournament ID
 * @param {boolean} isSuperAdmin - Whether the user is a super admin
 * @returns {Promise<boolean>} - True if user has access
 */
export const hasAccessToTournament = async (userId, tournamentId, isSuperAdmin) => {
  if (!userId || !tournamentId) return false;

  // Super admins have access to all tournaments
  if (isSuperAdmin) return true;

  // Check if user is specifically assigned as tournament admin
  return await isTournamentAdminForTournament(userId, tournamentId);
};
